import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {usePageDataContext} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {fetchWithTracing} from '../../helpers/fetch-with-tracing'
import {useMutation, useIsMutating, useQueryClient, useQuery} from '@github-ui/react-query'
import type {QueryObserverResult} from '@github-ui/react-query'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useEffect, useState} from 'react'
import {sleep} from '../../helpers/sleep'
import {usePageVisibility} from '../use-page-visibility'
import type {StackEntryPayload} from '../../page-data/payloads/merge-box'

type StackRebaseEntry = Omit<StackEntryPayload, 'status'>

type StackRebaseStatusPayload = {
  jobState: 'PENDING' | 'RUNNING' | 'DONE'
  pullRequestStates: Array<
    | {
        number: number
        state: 'SUCCEEDED' | 'CONFLICTED' | 'PENDING'
        message: null
      }
    | {
        number: number
        state: 'FAILED'
        message: string
      }
  >
}
export type StackRebasePullRequestStatus = StackRebaseStatusPayload['pullRequestStates'][0]['state']

export type StackRebasePullRequestEntry = {
  number: number
  state: StackRebasePullRequestStatus
  message: string | null
}

type Input = {
  entries: StackRebaseEntry[]
}
type Callbacks = {
  onSuccess: () => void
  onError: (e: Error) => void
}

// How long to keep showing the terminal rebase labels (Rebased / Merge
// conflicts / Rebase failed) on each entry after the rebase job finishes.
const REBASE_STATUS_CLEAR_DELAY_MS = 10000 // 10 seconds

const mutationKey = ['mergebox-stack-rebase']

function useStackRebaseStatusQueryKey() {
  const {basePageDataUrl} = usePageDataContext()
  return [PageData.stackRebaseStatus, `basePageDataURL:${basePageDataUrl}`]
}

function useStackRebaseStatusQuery() {
  const statusURL = usePageDataUrl(PageData.stackRebaseStatus)
  const queryKey = useStackRebaseStatusQueryKey()
  const queryClient = useQueryClient()

  return useQuery({
    queryKey,
    queryFn: async () => {
      return fetchWithTracing<StackRebaseStatusPayload>(statusURL, queryKey, queryClient)
    },
    staleTime: Infinity,

    // NOTE: this query is not run initially when the hook is called, instead
    // we explicitly call `refetch` from the mutation function.
    enabled: false,
  })
}

export function useStackRebaseStatus() {
  const isRebasing = useIsMutating({mutationKey}) > 0
  const {data} = useStackRebaseStatusQuery()

  const failedState = data?.pullRequestStates.find(
    pullState => pullState.state === 'CONFLICTED' || pullState.state === 'FAILED',
  )?.state

  const status = data?.pullRequestStates.reduce(
    (acc, pullState) => {
      if (pullState.state === 'PENDING' && !isRebasing && failedState) {
        // If the job is pending but we aren't currently rebasing, treat the status as the
        // error state found in another PR. This happens if a lower PR in the stack has merge conflicts.
        acc[pullState.number] = {number: pullState.number, state: failedState, message: null}
      } else {
        acc[pullState.number] = {number: pullState.number, state: pullState.state, message: pullState.message}
      }
      return acc
    },
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    {} as Record<number, StackRebasePullRequestEntry>,
  )

  // Once every entry has settled into a terminal state (nothing PENDING) and
  // the rebase mutation is no longer running, hide the per-entry rebase
  // labels (Rebased / Merge conflicts / Rebase failed) after a short delay so
  // they don't stick around until the next page refresh. We only start the
  // countdown while the page is visible: if the user is on another tab when
  // the rebase finishes, we wait until they come back so they always get to
  // see the terminal labels for the full delay.
  //
  // We only clear `displayStatus`, not the underlying query data, because the
  // summary banner reads from `status` and must remain visible until the user
  // dismisses it or starts another rebase.
  const isPageVisible = usePageVisibility()
  const hasPendingEntry = data?.pullRequestStates.some(pullState => pullState.state === 'PENDING') ?? true
  const isTerminal = !isRebasing && data !== undefined && !hasPendingEntry
  const [effectiveStatusesAvailable, setEffectiveStatusesAvailable] = useState(false)

  // Reset the cleared flag whenever we leave the terminal state (e.g. a new
  // rebase begins or the query data is removed by the mutation).
  if (!isTerminal && effectiveStatusesAvailable) {
    setEffectiveStatusesAvailable(false)
  }

  useEffect(() => {
    if (!isTerminal || !isPageVisible || effectiveStatusesAvailable) return
    const id = setTimeout(() => {
      setEffectiveStatusesAvailable(true)
    }, REBASE_STATUS_CLEAR_DELAY_MS)
    return () => clearTimeout(id)
  }, [isTerminal, isPageVisible, effectiveStatusesAvailable])

  return {
    isRebasing,
    status: status ?? {},
    displayStatus: effectiveStatusesAvailable ? {} : (status ?? {}),
  }
}

export function useStackRebaseMutation({onSuccess, onError}: Callbacks) {
  const apiURL = usePageDataUrl(PageData.stackRebase)
  const {refetch: refetchStatus} = useStackRebaseStatusQuery()
  const queryClient = useQueryClient()
  const statusQueryKey = useStackRebaseStatusQueryKey()

  return useMutation<Response, Error, Input>({
    mutationKey,
    mutationFn: async ({entries}) => {
      const expectedHeadOids = entries.reduce(
        (acc, entry) => {
          if (entry.pull.state !== 'MERGED') {
            acc[entry.pull.number.toString()] = entry.pull.headOid
          }
          return acc
        },
        {} as Record<string, string>,
      )

      // Clear any old state from a previous rebase so we don't show
      // stale results between now and when we first load the new status.
      queryClient.removeQueries({queryKey: statusQueryKey})

      return fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {expectedHeadOids},
      })
    },
    onSuccess: async data => {
      const json = await parseJSONWithBetterErrors(data)
      if (!data.ok) {
        throwErrorsIfBadResponse(data, json)
      }

      await pollForResults(refetchStatus)

      // Without a timeout, this would be a race condition sometimes. You could hit the
      // server without the serverside update being
      setTimeout(() => onSuccess(), 1000)
    },
    onError: (e: Error) => {
      onError(e)
    },
  })
}

export const POLL_INITIAL_DELAY = 1000
const POLL_BACKOFF_MULTIPLIER = 1.5
const POLL_TIMEOUT = 600_000

async function pollForResults(
  fetchStatus: (options: {throwOnError: boolean}) => Promise<QueryObserverResult<StackRebaseStatusPayload>>,
  initialDelay = POLL_INITIAL_DELAY,
): Promise<void> {
  const startTime = Date.now()
  let delay = initialDelay

  for (;;) {
    const status = (await fetchStatus({throwOnError: true})).data
    if (!status) {
      throw new Error('Failed to load stack rebase status')
    }

    if (status.jobState === 'DONE') {
      return
    }

    if (Date.now() - startTime + delay > POLL_TIMEOUT) {
      throw new Error('Stack rebase polling timed out')
    }

    await sleep(delay)
    delay *= POLL_BACKOFF_MULTIPLIER
  }
}
